import { Link } from 'react-router-dom';
import { FileText, CheckSquare, Heart, Mountain, Compass, Sun } from 'lucide-react';
import { useEntries, useEntryStats } from '@/hooks/use-entries';
import { useTaskStats } from '@/hooks/use-tasks';
import { EntryCard } from '@/components/entries';
import { Card, CardHeader, CardContent } from '@/components/ui';

export function HomePage() {
  const { data, isLoading } = useEntries({ sortBy: 'recordedAt', sortOrder: 'desc' });
  const { data: entryStats } = useEntryStats();
  const { data: taskStats } = useTaskStats();

  const recentEntries = data?.pages[0]?.entries.slice(0, 5) ?? [];

  // Get time-based greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div data-testid="home-page" className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Values-aligned welcome header */}
      <div className="mb-8">
        <h1 data-testid="home-page-title" className="text-3xl font-display font-bold text-stone-900">{greeting}</h1>
        <p className="text-stone-600 mt-2 text-lg">
          What matters most today?
        </p>
      </div>

      {/* Daily intention card */}
      <Card data-testid="morning-briefing-card" className="mb-8 bg-gradient-to-r from-primary-600 to-primary-700 border-0 shadow-lg overflow-hidden">
        <div className="p-6 relative">
          {/* Decorative mountain silhouette */}
          <div className="absolute right-0 bottom-0 opacity-10">
            <Mountain className="w-32 h-32 text-white" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="w-5 h-5 text-accent-300" />
              <span className="text-primary-100 text-sm font-medium uppercase tracking-wide">Daily Reminder</span>
            </div>
            <p className="text-xl md:text-2xl text-white font-display italic leading-relaxed">
              "Choose courage over comfort. Do the right thing—you'll feel better."
            </p>
            <p className="text-primary-200 mt-3 text-sm">
              The wilderness awaits. What hard thing will you tackle today?
            </p>
          </div>
        </div>
      </Card>

      {/* Quick stats - Values-aligned */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary-100 rounded-xl">
              <FileText className="w-5 h-5 text-primary-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">
                {entryStats?.total ?? '--'}
              </p>
              <p className="text-sm text-stone-500">Entries</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-accent-100 rounded-xl">
              <CheckSquare className="w-5 h-5 text-accent-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">
                {taskStats?.due_today_count ?? '--'}
              </p>
              <p className="text-sm text-stone-500">Due today</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-rose-100 rounded-xl">
              <Heart className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">
                {taskStats?.overdue_count ?? '--'}
              </p>
              <p className="text-sm text-stone-500">Need attention</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-xl">
              <Sun className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">
                {taskStats?.by_status?.completed ?? '--'}
              </p>
              <p className="text-sm text-stone-500">Completed</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent entries */}
      <Card className="border-stone-200">
        <CardHeader className="border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-display font-semibold text-stone-900">
              Recent Entries
            </h2>
            <Link
              to="/entries"
              className="text-sm text-primary-700 hover:text-primary-800 font-medium"
            >
              View all →
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-stone-500">Loading...</div>
          ) : recentEntries.length === 0 ? (
            <div className="p-8 text-center">
              <Mountain className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500">No entries yet</p>
              <p className="text-sm text-stone-400 mt-1">Your entries will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {recentEntries.map((entry) => (
                <div key={entry.id} className="px-4 py-2">
                  <EntryCard entry={entry} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
